/**
 * Asset Create Page.
 *
 * Form for creating a new inventory asset. Auto-generates SKU and
 * barcode on the backend after submission.
 */

import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Search, CheckCircle, AlertCircle, Save } from "lucide-react";
import { useUnsavedChanges } from "@/hooks/useUnsavedChanges";
import UnsavedChangesDialog from "@/components/ui/UnsavedChangesDialog";
import { useMutation } from "@tanstack/react-query";
import { useCreateAsset } from "@/features/inventory/api";
import { toast } from "@/stores/toastStore";
import { apiClient } from "@/api/client";
import AssetNameBuilder from "@/components/ui/AssetNameBuilder";
import CategorySelect from "@/components/ui/CategorySelect";
import Badge from "@/components/ui/Badge";
import Button from "@/components/ui/Button";
import Input from "@/components/ui/Input";
import Card from "@/components/ui/Card";
import LocationComboSelect from "@/components/ui/LocationComboSelect";

function AssetCreatePage() {
  const navigate = useNavigate();
  const skipBlockRef = useRef(false);
  const createMutation = useCreateAsset();

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    category: "equipment",
    year: "",
    make: "",
    model: "",
    variant: "",
    serial_number: "",
    vin: "",
    lot_number: "",
    location: "",
    purchase_price: "",
  });
  const [customFields, setCustomFields] = useState<Record<string, string>>({});
  const [error, setError] = useState("");
  const [vinDecoded, setVinDecoded] = useState<{
    valid: boolean;
    error?: string | null;
    decoded: Record<string, string>;
    asset_fields: Record<string, unknown>;
  } | null>(null);

  const vinDecodeMutation = useMutation({
    mutationFn: async (vin: string) =>
      (await apiClient.get(`/api/v1/vin/decode/${vin}`)).data,
    onSuccess: (data) => {
      setVinDecoded(data);
      if (data.valid && data.asset_fields) {
        const af = data.asset_fields;
        setFormData((prev) => ({
          ...prev,
          name: af.suggested_name || prev.name,
          year: af.year ? String(af.year) : prev.year,
          make: af.make || prev.make,
          model: af.model || prev.model,
          category: af.suggested_category || prev.category,
        }));
      }
    },
  });

  // --- Unsaved changes protection ---
  const isDirty =
    !skipBlockRef.current &&
    (formData.name !== "" ||
      formData.description !== "" ||
      formData.year !== "" ||
      formData.make !== "" ||
      formData.model !== "" ||
      formData.variant !== "" ||
      formData.serial_number !== "" ||
      formData.vin !== "" ||
      formData.lot_number !== "" ||
      formData.location !== "" ||
      formData.purchase_price !== "");

  const { isBlocked, proceed, reset, checkUnsaved } =
    useUnsavedChanges(isDirty);

  const saveDraft = async () => {
    if (!formData.name) {
      toast.error("Asset name is required to save draft");
      return;
    }
    skipBlockRef.current = true;
    try {
      const asset = await createMutation.mutateAsync({
        name: formData.name,
        description: formData.description || undefined,
        category: formData.category,
        year: formData.year ? parseInt(formData.year) : undefined,
        make: formData.make || undefined,
        model: formData.model || undefined,
        variant: formData.variant || undefined,
        serial_number: formData.serial_number || undefined,
        vin: formData.vin || undefined,
        lot_number: formData.lot_number || undefined,
        location: formData.location || undefined,
        purchase_price: formData.purchase_price
          ? parseFloat(formData.purchase_price)
          : undefined,
        custom_fields:
          Object.keys(customFields).length > 0 ? customFields : undefined,
      });
      toast.success("Draft saved", asset.sku);
      navigate(`/inventory/${asset.id}`);
    } catch {
      skipBlockRef.current = false;
      toast.error("Failed to save draft");
    }
  };

  const handleDecodeVin = () => {
    const vin = formData.vin.trim();
    if (vin.length === 17) {
      vinDecodeMutation.mutate(vin);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    try {
      const asset = await createMutation.mutateAsync({
        name: formData.name,
        description: formData.description || undefined,
        category: formData.category,
        year: formData.year ? parseInt(formData.year) : undefined,
        make: formData.make || undefined,
        model: formData.model || undefined,
        variant: formData.variant || undefined,
        serial_number: formData.serial_number || undefined,
        vin: formData.vin || undefined,
        lot_number: formData.lot_number || undefined,
        location: formData.location || undefined,
        purchase_price: formData.purchase_price
          ? parseFloat(formData.purchase_price)
          : undefined,
        custom_fields:
          Object.keys(customFields).length > 0 ? customFields : undefined,
      });
      skipBlockRef.current = true;
      navigate(`/inventory/${asset.id}`);
    } catch {
      setError("Failed to create asset. Please check the form and try again.");
    }
  };

  return (
    <div className="page-form space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-[var(--text-primary)]">
          Add New Asset
        </h2>
        <p className="mt-1 text-sm text-[var(--text-secondary)]">
          A unique SKU and barcode will be automatically generated.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {error && (
          <div className="rounded-[var(--radius-md)] bg-[var(--fill-danger)] p-3 text-sm text-[var(--text-danger)]">
            {error}
          </div>
        )}

        {/* Basic Info */}
        <Card>
          <h3 className="mb-4 text-sm font-medium text-[var(--text-primary)]">
            Basic Information
          </h3>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <AssetNameBuilder
                value={formData.name}
                onChange={(name) => setFormData((prev) => ({ ...prev, name }))}
                category={formData.category}
                initialYear={formData.year}
                initialMake={formData.make}
                initialModel={formData.model}
                initialVariant={formData.variant}
                initialCustomFields={customFields}
                onFieldsChange={(fields) => {
                  setFormData((prev) => ({
                    ...prev,
                    ...(fields.year !== undefined && {
                      year: fields.year || "",
                    }),
                    ...(fields.make !== undefined && {
                      make: fields.make || "",
                    }),
                    ...(fields.model !== undefined && {
                      model: fields.model || "",
                    }),
                    ...(fields.variant !== undefined && {
                      variant: fields.variant || "",
                    }),
                  }));
                }}
                onCategoryChange={(cat) =>
                  setFormData((prev) => ({ ...prev, category: cat }))
                }
                onCustomFieldsChange={setCustomFields}
              />
            </div>
            <div>
              <CategorySelect
                label="Category"
                value={formData.category}
                onChange={(v) =>
                  setFormData((prev) => ({ ...prev, category: v }))
                }
              />
            </div>
            <LocationComboSelect
              label="Location"
              value={formData.location}
              onChange={(v) =>
                setFormData((prev) => ({ ...prev, location: v }))
              }
              placeholder="Search or type a location..."
            />
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-[var(--text-secondary)]">
                Description
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={3}
                placeholder="Optional notes about this asset"
                className="mt-1 block w-full rounded-[var(--radius-md)] border border-[var(--border-default)] px-3 py-2 text-sm focus:border-[var(--border-focus)] focus:outline-none focus-visible:shadow-[var(--focus-ring)]"
              />
            </div>
          </div>
        </Card>

        {/* Equipment Details */}
        <Card>
          <h3 className="mb-4 text-sm font-medium text-[var(--text-primary)]">
            Equipment Details
          </h3>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Input
              label="Year"
              name="year"
              type="number"
              value={formData.year}
              onChange={handleChange}
              placeholder="e.g., 2022"
            />
            <Input
              label="Make"
              name="make"
              value={formData.make}
              onChange={handleChange}
              placeholder="e.g., John Deere"
            />
            <Input
              label="Model"
              name="model"
              value={formData.model}
              onChange={handleChange}
              placeholder="e.g., 6120M"
            />
            <Input
              label="Variant"
              name="variant"
              value={formData.variant}
              onChange={handleChange}
              placeholder="e.g., Premium, MFWD"
            />
            <Input
              label="Serial Number"
              name="serial_number"
              value={formData.serial_number}
              onChange={handleChange}
            />
            <Input
              label="Lot Number"
              name="lot_number"
              value={formData.lot_number}
              onChange={handleChange}
              placeholder="Optional"
            />
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-[var(--text-primary)]">
                VIN
              </label>
              <div className="mt-1 flex gap-2">
                <input
                  name="vin"
                  value={formData.vin}
                  onChange={handleChange}
                  placeholder="17-character VIN"
                  maxLength={17}
                  className="flex-1 h-9 rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-[var(--color-glass)] px-3 text-sm text-[var(--text-primary)] uppercase tracking-wider font-mono placeholder:text-[var(--text-tertiary)] placeholder:font-sans placeholder:normal-case placeholder:tracking-normal focus:outline-none focus:ring-2 focus:ring-brand-500/40"
                />
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  onClick={handleDecodeVin}
                  isLoading={vinDecodeMutation.isPending}
                  disabled={formData.vin.trim().length !== 17}
                >
                  <Search className="h-3.5 w-3.5" /> Decode
                </Button>
              </div>
              {vinDecoded && (
                <div
                  className={`mt-2 rounded-[var(--radius-lg)] border p-2.5 text-xs ${
                    vinDecoded.valid
                      ? "border-[var(--text-success)]/30 bg-[var(--fill-success)]/50 dark:border-emerald-800 dark:bg-emerald-900/10"
                      : "border-[var(--text-danger)]/30 bg-[var(--fill-danger)]/50 dark:border-red-800 dark:bg-red-900/10"
                  }`}
                >
                  {vinDecoded.valid ? (
                    <div>
                      <div className="flex items-center gap-1.5 mb-1.5">
                        <CheckCircle className="h-3.5 w-3.5 text-[var(--text-success)]" />
                        <span className="font-medium text-[var(--text-success)] dark:text-emerald-300">
                          VIN decoded — fields auto-filled
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {Object.entries(vinDecoded.decoded)
                          .slice(0, 8)
                          .map(([k, v]) => (
                            <Badge key={k} variant="default">
                              {k}: {v}
                            </Badge>
                          ))}
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1.5">
                      <AlertCircle className="h-3.5 w-3.5 text-[var(--text-danger)]" />
                      <span className="text-[var(--text-danger)] dark:text-red-300">
                        {vinDecoded.error || "VIN not recognized"}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </Card>

        {/* Financial */}
        <Card>
          <h3 className="mb-4 text-sm font-medium text-[var(--text-primary)]">
            Financials
          </h3>
          <div className="max-w-xs">
            <Input
              label="Purchase Price"
              name="purchase_price"
              type="number"
              step="0.01"
              min="0"
              value={formData.purchase_price}
              onChange={handleChange}
              placeholder="0.00"
            />
          </div>
        </Card>

        {/* Actions */}
        <div className="flex justify-end gap-3">
          <Button
            type="button"
            variant="secondary"
            onClick={() => checkUnsaved(() => navigate("/inventory"))}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={saveDraft}
          >
            <Save className="h-3.5 w-3.5" /> Save Draft
          </Button>
          <Button type="submit" isLoading={createMutation.isPending}>
            Create Asset
          </Button>
        </div>
      </form>

      <UnsavedChangesDialog
        isOpen={isBlocked}
        onSaveDraft={saveDraft}
        onDiscard={proceed}
        onStay={reset}
        entityName="asset"
      />
    </div>
  );
}

export default AssetCreatePage;
